import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { pluck, map } from 'rxjs/operators';
import { getTableConfig } from '@app/pages/cmms/graphql/cmms-table.queries';
import { ICMMSColumn } from '@shared/ui-components/au-table-layout/utils/interfaces';
import {
	ICMMSTableConfig,
	ICMMSTableConfigQuery,
} from '@app/pages/cmms/interfaces/cmms-table.interfaces';
import { setIncidentForEditing } from '@app/pages/cmms/components/incident/state/incidents.actions';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import { CmmsPages, ICmmsState } from '@app/pages/cmms/state/cmms.reducer';
import { Action, Store } from '@ngrx/store';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { setWorkOrderForEditing } from '@app/pages/cmms/components/work-order/state/work-order.actions';
import { setRequestForEditing } from '@app/pages/cmms/components/request/state/requests.action';
import { TableType } from '../types';

export interface IHeaderEventsItem {
	classificationFiltration: string;
}

@Injectable({
	providedIn: 'root',
})
export class CmmsTableService {
	constructor(
		private apollo: Apollo,
		private store$: Store<ICmmsState>,
		private sidebarService: AuRightSidebarService
	) {}

	headerEventsStream = new BehaviorSubject<IHeaderEventsItem>({
		classificationFiltration: 'BCT',
	});

	getTableColumnsConfig(tableType: TableType): Observable<ICMMSTableConfig> {
		return this.apollo
			.query<ICMMSTableConfigQuery>({
				query: getTableConfig,
				variables: {
					tableType,
				},
			})
			.pipe(
				pluck('data', 'tableConfig'),
				map(columnsConfig => {
					const { columns, filtersConfig, isExportable } = columnsConfig;
					const normalizedColumns = columns.map(column => {
						const updatedColumnsConfig = { ...column };
						if (
							updatedColumnsConfig.customData &&
							typeof updatedColumnsConfig.customData === 'string'
						) {
							updatedColumnsConfig.customData = JSON.parse(
								updatedColumnsConfig.customData
							);
						}
						// Convert here json format to object that we can use
						return updatedColumnsConfig as ICMMSColumn;
					});
					return {
						columns: normalizedColumns,
						filters: filtersConfig,
						isExportable,
					};
				})
			);
	}

	navigateToEntityFromLinkedList(navigationEvent): void {
		if (navigationEvent.workOrderId) {
			this.navigateToWorkOrder(navigationEvent);
		} else if (navigationEvent.incidentId) {
			this.navigateToIncident(navigationEvent);
		} else if (navigationEvent.requestId) {
			this.navigateToRequest(navigationEvent);
		}
	}

	// TODO Make later workOrderEdit, incidentEdit, requestEdit as Partial<T>
	//  Its actually partial and obligates only private key
	private navigateToWorkOrder(navigationEvent): void {
		const title = {
			text: 'workorder_id',
			id: navigationEvent.workOrderId,
		};
		const pk = navigationEvent.pk;
		// @ts-ignore
		const action = setWorkOrderForEditing({ workOrderEdit: { pk } });
		this.openCmmsPage(CmmsPages.editWorkOrder, action, title);
	}

	private navigateToIncident(navigationEvent): void {
		const title = {
			text: 'incident_id',
			id: navigationEvent.incidentId,
		};
		const pk = navigationEvent.pk;
		// @ts-ignore
		const action = setIncidentForEditing({ incidentEdit: { pk } });
		this.openCmmsPage(CmmsPages.editIncident, action, title);
	}

	private navigateToRequest(navigationEvent): void {
		const title = {
			text: 'request_id',
			id: navigationEvent.requestId,
		};
		const pk = navigationEvent.pk;
		// @ts-ignore
		const action = setRequestForEditing({ requestEdit: { pk } });
		this.openCmmsPage(CmmsPages.editRequest, action, title);
	}

	private openCmmsPage(
		cmmsPage: CmmsPages,
		action: Action,
		title: { text: string; id?: string | number }
	) {
		this.store$.dispatch(action);

		this.sidebarService.setComponentAndOpen(
			ACTIVE_PAGE_CONFIG[cmmsPage].createComponent,
			{
				...ACTIVE_PAGE_CONFIG[cmmsPage].sidebarConfig,
				title,
			}
		);
	}
}
